import React, { Fragment, useState, useEffect } from 'react';
import ChatGPTPresetDrawer from '../ChatGPTPresetDrawer';
import { connect } from 'react-redux';
import { toggleControl } from '../../../../store/control/duck';
import { fetchPolicy } from '../../../../store/policy/api';
import { injectIntl } from 'react-intl';
import { Grid, Typography, Box, FormControl } from '@mui/material';
import ActionButton from '../ActionButton/ActionButton';
import { decreaseGPTQuota, handleError, resetGPTQuota, validateGPTQuota } from '../../../../utils/openai';
import LoadingBackdrop from '../../../LoadingBackdrop/LoadingBackdrop';
import { useForceUpdate } from '../../../../utils/hooks';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import GPTProductCategoryCard from './GPTProductCategoryCard';
import { getProductRecommendationV2 } from '../../../../store/openai/apis/product-recommendation-v2';
import { ClientLifeStageOptionPicker, FreeTextArea, DISCPicker, getTraitsFromDisc } from '../Inputs/Inputs';
import { useRevealResults } from '../useRevealResults';

const GPTProductRecommendationDrawerV2 = props => {
  const { intl, gptProductRecommendationV2Drawer, toggleControl } = props;
  const [result, setResult] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const forceUpdate = useForceUpdate();
  const { showSendButton, client, open } = gptProductRecommendationV2Drawer || {};
  const [portfolios, setPortfolios] = useState([]);
  const [options, setOptions] = useState({});
  const [traits, setTraits] = useState({});
  const { bodyRef, resultsRef, reveal } = useRevealResults();

  useEffect(() => {
    setResult(gptProductRecommendationV2Drawer?.result || undefined);
  }, [gptProductRecommendationV2Drawer?.result]);

  useEffect(() => {
    if (client?._id) {
      fetchPolicy({ clientId: client?._id }).then(res => {
        const portfolios = res?.portfolio || [];
        setPortfolios(portfolios);
      });
      const traits = getTraitsFromDisc(client?.factsheetId?.personality || '');
      setTraits(traits);
      setOptions({
        ...options,
        personality: client?.factsheetId?.personality,
        lifeStage: client?.factsheetId?.promptInput?.lifeStage
      });
    }
  }, [client?._id, fetchPolicy]);

  const isOpen = !!gptProductRecommendationV2Drawer && open;
  const onGetRecommendation = async () => {
    if (!client) {
      return;
    }

    try {
      validateGPTQuota();
      setLoading(true);
      const clientOptions = { ...options };
      const additionalInformation =
        options.freeText && options.freeText.trim() !== '' ? options.freeText.trim() : undefined;
      if (additionalInformation) {
        clientOptions.additionalInformation = additionalInformation;
      }
      delete clientOptions.freeText;
      const payload = {
        userObjectId: client._id,
        clientObjectId: client._id,
        factsheets: client.factsheetId,
        portfolios: portfolios,
        clientTraits: clientOptions,
        language: intl.locale,
        topN: 5,
        returnPrompt: false
      };

      const res = await getProductRecommendationV2(payload);
      const products = res.products;
      setResult(products);
      reveal();
      decreaseGPTQuota();
    } catch (error) {
      handleError(error);
      setResult(undefined);
    } finally {
      setLoading(false);
    }
  };

  const onFeedback = () => {
    toggleControl('chatGPTFeedbackDialog', {
      client: client,
      extraContent: result,
      onSubmit: () => {
        resetGPTQuota();
        forceUpdate();
      }
    });
  };

  const handleCopyReason = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    toggleControl('gptProductRecommendationV2Drawer', { open: false });
    setTraits({});
    setOptions({});
    setResult(undefined);
    setLoading(false);
    setCopied(false);
  };

  const handleLazypackMatching = product => {
    toggleControl('ChatGPTPersonalAnalysis', {
      client,
      type: 'product',
      open: true,
      payload: {
        product: product,
        options: options,
        traits: traits
      }
    });
  };

  const currentClientLifeStage = options.lifeStage;

  return (
    <Fragment key={props.key}>
      <FormControl>
        <LoadingBackdrop open={loading} />
        <ChatGPTPresetDrawer
          open={isOpen}
          onClose={handleClose}
          onExited={handleClose}
          onFeedback={onFeedback}
          title={intl.formatMessage({ id: 'gpt-options-product-recommendation-title' })}
          subtitle={intl.formatMessage({ id: 'gpt-subtitle-product-recommendation' })}
          showQuota
          bodyRef={bodyRef}
        >
          <Grid
            container
            spacing={2}
            wrap="nowrap"
            // flexShrink 0: the drawer body is the one scroller, so this column
            // must be as tall as its content rather than squeezed to fit the body.
            style={{ minHeight: '100%', flexShrink: 0, boxSizing: 'border-box' }}
            sx={{ flexDirection: 'column' }}
          >
            {/* Description */}
            <Grid>
              <Typography style={{ whiteSpace: 'pre-wrap', marginBottom: '16px', color: '#666' }}>
                {intl.formatMessage({ id: 'gpt-options-product-recommendation-drawer-content' })}
              </Typography>
            </Grid>

            <Grid>
              <ClientLifeStageOptionPicker
                intl={intl}
                options={options}
                onValueChange={(field, value, oldValue) => {
                  setOptions({ ...options, [field]: value });
                }}
              />

              <FreeTextArea
                intl={intl}
                value={options.freeText}
                onChange={(field, value, oldValue) => {
                  setOptions({ ...options, [field]: value });
                }}
              />
            </Grid>

            {/* Send Button (conditional) */}
            {showSendButton && (
              <Grid>
                <ActionButton onClick={() => {}} disabled>
                  <Typography>{intl.formatMessage({ id: 'gpt-options-product-recommendation-send' })}</Typography>
                </ActionButton>
              </Grid>
            )}

            <ActionButton onClick={onGetRecommendation} disabled={!currentClientLifeStage} style={{ margin: '8px 0' }}>
              {intl.formatMessage({ id: 'gpt-options-product-recommendation-send' })}
            </ActionButton>

            {/* Product Cards — no scroller of their own; see useRevealResults. */}
            {result && result.length > 0 && (
              <Grid ref={resultsRef}>
                <Typography style={{ color: '#333', marginBottom: '16px' }}>
                  {intl.formatMessage({ id: 'gpt-product-recommendation-reasons-title' })}
                </Typography>
                {result.map((product, index) => (
                  <GPTProductCategoryCard
                    key={index}
                    product={product}
                    intl={intl}
                    displayMode="focused"
                    onCopyReason={handleCopyReason}
                    onLazypackMatching={handleLazypackMatching}
                  />
                ))}
              </Grid>
            )}

            {/* LLM Info (subtle display) */}
            {result && result.llmModel && (
              <Grid>
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px'
                  }}
                >
                  <InfoOutlined style={{ fontSize: 16, color: '#999' }} />
                  <Typography variant="caption" style={{ color: '#999' }}>
                    {result.llmModel} • {result.llmTime?.toFixed(2)}s
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Copy feedback */}
            {copied && (
              <Box
                style={{
                  position: 'fixed',
                  bottom: '80px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#333',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px'
                }}
              >
                <Typography variant="body2">Copied!</Typography>
              </Box>
            )}
          </Grid>
        </ChatGPTPresetDrawer>
      </FormControl>
    </Fragment>
  );
};

const container = connect(
  state => ({
    gptProductRecommendationV2Drawer: state.control.gptProductRecommendationV2Drawer,
    language: state.user.language,
    clientDetails: state.client.clientDetails,
    clientData: state.client.clientData
  }),
  {
    toggleControl
  }
)(GPTProductRecommendationDrawerV2);

export default injectIntl(container);
